// Integração GHL — configuração das colunas-gatilho e do de-para de campos.
// Estas tabelas (ghl_stage_map / ghl_field_map) são lidas pelo pipeline de captura
// do dashboard (dashboard-orbion-demo): estágio batido -> evento -> escrita no banco.
// O desk é apenas o EDITOR da configuração — não processa eventos.
import { supabase } from '../lib/supabase';
import { logError } from '../lib/logError';
import { resolverProduto, normalizarRotulo } from './produtoResolver';

// Sem token aqui de proposito. A leitura do GHL passa pelas rotas server-side
// (api/ghl-pipelines.js e api/ghl-custom-fields.js), que guardam GHL_API_TOKEN — sem prefixo
// VITE_, portanto fora do bundle. Ate 2026-08-05 este arquivo usava VITE_GHL_API_TOKEN e
// chamava o GHL direto do navegador: como todo VITE_* e embutido no bundle, o token da API do
// CRM ficava legivel para qualquer pessoa que abrisse o DevTools do desk.

/** Eventos que uma coluna-gatilho pode disparar. '' = não é gatilho. */
export const EVENTOS = [
    { value: '',               label: '— não é gatilho —' },
    { value: 'agendamento',    label: 'Agendamento' },
    { value: 'comparecimento', label: 'Comparecimento' },
    { value: 'no_show',        label: 'No-show' },
    { value: 'reagendamento',  label: 'Reagendamento' },
    { value: 'venda',          label: 'Venda realizada' },
    { value: 'venda_perdida',  label: 'Venda perdida' },
    { value: 'ignorar',        label: 'Ignorar (explícito)' },
];

/** Colunas de destino permitidas no de-para (whitelist — nunca texto livre). */
export const DESTINOS = [
    { tabela: 'ghl_reunioes', coluna: 'agendada_em',   label: 'Reunião → Data do agendamento' },
    { tabela: 'ghl_reunioes', coluna: 'data_reuniao',  label: 'Reunião → Data da reunião' },
    { tabela: 'ghl_reunioes', coluna: 'hora_reuniao',  label: 'Reunião → Hora da reunião' },
    { tabela: 'ghl_reunioes', coluna: 'sdr_nome',      label: 'Reunião → Quem agendou (SDR)' },
    { tabela: 'ghl_reunioes', coluna: 'closer_nome',   label: 'Reunião → Closer da reunião' },
    { tabela: 'ghl_reunioes', coluna: 'fonte',         label: 'Reunião → Fonte' },
    { tabela: 'ghl_reunioes', coluna: 'nicho',         label: 'Reunião → Nicho' },
    { tabela: 'ghl_reunioes', coluna: 'empresa',       label: 'Reunião → Empresa' },
    { tabela: 'ghl_reunioes', coluna: 'cargo_lead',    label: 'Reunião → Cargo do lead' },
    { tabela: 'ghl_vendas',   coluna: 'valor_venda',   label: 'Venda → Valor da venda' },
    { tabela: 'ghl_vendas',   coluna: 'produto',       label: 'Venda → Produto vendido' },
    { tabela: 'ghl_vendas',   coluna: 'forma_pagamento', label: 'Venda → Forma de pagamento' },
    { tabela: 'ghl_vendas',   coluna: 'descricao',     label: 'Venda → Descrição da venda' },
    { tabela: 'ghl_vendas',   coluna: 'fonte',         label: 'Venda → Fonte' },
];

// ─── GHL (leitura via proxy /ghl-api — dev: vite proxy; prod: rewrite Vercel) ──

export async function fetchGhlPipelines() {
    const res = await fetch('/api/ghl-pipelines');
    if (!res.ok) throw new Error(`GHL pipelines: HTTP ${res.status}`);
    const json = await res.json();
    return json.pipelines ?? [];
}

export async function fetchGhlCustomFields() {
    const res = await fetch('/api/ghl-custom-fields?model=opportunity');
    if (!res.ok) throw new Error(`GHL customFields: HTTP ${res.status}`);
    const json = await res.json();
    return (json.customFields ?? []).filter((f) => f.model === 'opportunity');
}

/** Nome (minúsculo) do custom field que guarda o dropdown de produto. Id em produção:
 *  `DUu8KXWa4HygNREHMlOR`. Buscamos por NOME e não por id fixo para o desk não quebrar se a
 *  location for recriada — o id muda, o nome não. */
export const CAMPO_PRODUTO_VENDIDO = 'produto vendido';

/** Opções de um custom field, como o GHL as devolve: ora strings, ora `{ value, label }`. */
export function opcoesDoCampoGhl(field) {
    const brutas = field?.picklistOptions ?? field?.options ?? [];
    if (!Array.isArray(brutas)) return [];
    return brutas
        .map((o) => (typeof o === 'string' ? o : o?.value ?? o?.label))
        .filter((v) => typeof v === 'string' && v.trim() !== '');
}

/** Opções de Produto e Forma de Pagamento a partir dos custom fields do GHL
 *  (para os selects da edição de venda). Tolerante a falha: retorna listas vazias. */
export async function getGhlOpcoesVenda() {
    try {
        const fields = await fetchGhlCustomFields();
        const byName = (n) => fields.find((f) => (f.name || '').trim().toLowerCase() === n);
        return {
            produtos: opcoesDoCampoGhl(byName(CAMPO_PRODUTO_VENDIDO)),
            formasPagamento: opcoesDoCampoGhl(byName('forma de pagamento')),
        };
    } catch (e) {
        logError(e, { fn: 'getGhlOpcoesVenda' });
        return { produtos: [], formasPagamento: [] };
    }
}

// ─── Catálogo de produtos: desk → GHL ─────────────────────────────────────────
//
// O dropdown "Produto Vendido" do GHL é a ORIGEM dos rótulos que chegam na venda; o catálogo do
// desk (`app_settings.produtos_config`) é quem sabe traduzir rótulo em produto. Enquanto os dois
// eram mantidos à mão, opção que existia só no GHL chegava sem produto correspondente — e o
// fallback antigo pagava o último item da lista, sem erro e sem log (ver `produtoResolver.js`).
// Sem fallback, esse mesmo rótulo passa a ser recusado na cara do operador. Este bloco existe
// para o rótulo órfão não nascer: o desk empurra o próprio catálogo para o GHL.

/** Rótulo canônico de um produto do desk — é ele que vira opção no dropdown do GHL. */
function rotuloDoProduto(produto) {
    const label = String(produto?.label ?? '').trim();
    return label || String(produto?.slug ?? '').trim();
}

/** Lista de opções do GHL saneada: strings verbatim, sem vazias, sem repetidas. */
function normalizarOpcoesGhl(opcoesGhl) {
    const brutas = Array.isArray(opcoesGhl) ? opcoesGhl : [];
    const vistas = new Set();
    const saida = [];
    for (const bruta of brutas) {
        const valor = typeof bruta === 'string' ? bruta : bruta?.value ?? bruta?.label;
        if (typeof valor !== 'string' || valor.trim() === '') continue;
        const chave = normalizarRotulo(valor);
        if (vistas.has(chave)) continue;
        vistas.add(chave);
        saida.push(valor);
    }
    return saida;
}

/**
 * Compara o catálogo do desk com o dropdown do GHL. PURA — não toca em rede, é a função que os
 * testes exercitam e a tela usa para desenhar o diff antes de o operador confirmar a escrita.
 *
 * As duas direções NÃO usam o mesmo critério, de propósito:
 *
 *  - `faltamNoGhl` compara por LABEL. É o que a sincronização vai adicionar como opção nova, e
 *    o dropdown tem que oferecer o rótulo canônico do produto. Alias não conta: `treinamentos_orbion`
 *    aceita "PD", mas se o GHL só tivesse "PD" o desk ainda quereria criar "Treinamentos Orbion".
 *  - `faltamNoDesk` usa `resolverProduto`, então ALIAS CONTA. "PD" é alias declarado de
 *    `treinamentos_orbion` (decisão do dono: é o mesmo produto), logo não está faltando.
 *    Usar o resolvedor de verdade é o ponto: o diff mostra o que o sistema realmente faz com o
 *    rótulo, não uma segunda opinião que pode divergir dele.
 *
 * Corolário: rodar isto contra um catálogo LEGADO (`keywords`, sem `aliases`) devolve o
 * resultado da regra de substring, que é justamente onde estão os defeitos medidos — "PCL" e
 * "Treinamentos Orbion" aparecem em `faltamNoDesk` porque hoje eles de fato não resolvem,
 * chegam ao fallback. Isso é o diagnóstico, não um bug do diff.
 *
 * @param {Array<{slug: string, label?: string, aliases?: string[], keywords?: string[]}>} produtos
 * @param {Array<string|{value?: string, label?: string}>} opcoesGhl
 * @returns {{faltamNoGhl: string[], faltamNoDesk: string[], emAmbos: Array<{opcao: string, slug: string, motivo: string}>}}
 */
export function diffCatalogoProdutos(produtos, opcoesGhl) {
    const lista = Array.isArray(produtos) ? produtos : [];
    const opcoes = normalizarOpcoesGhl(opcoesGhl);
    const chavesGhl = new Set(opcoes.map(normalizarRotulo));

    const faltamNoGhl = [];
    const jaListados = new Set();
    for (const produto of lista) {
        const rotulo = rotuloDoProduto(produto);
        if (!rotulo) continue;
        const chave = normalizarRotulo(rotulo);
        if (chavesGhl.has(chave) || jaListados.has(chave)) continue;
        jaListados.add(chave);
        faltamNoGhl.push(rotulo);
    }

    const faltamNoDesk = [];
    const emAmbos = [];
    for (const opcao of opcoes) {
        const r = resolverProduto(opcao, lista);
        if (r.slug) emAmbos.push({ opcao, slug: r.slug, motivo: r.motivo });
        else faltamNoDesk.push(opcao);
    }

    return { faltamNoGhl, faltamNoDesk, emAmbos };
}

/** Lê o custom field "Produto Vendido" inteiro (id, nome, tipo e opções atuais). */
export async function getGhlCampoProdutoVendido() {
    const fields = await fetchGhlCustomFields();
    const campo = fields.find((f) => (f.name || '').trim().toLowerCase() === CAMPO_PRODUTO_VENDIDO);
    if (!campo) throw new Error('GHL: custom field "Produto Vendido" não encontrado');
    return {
        id: campo.id,
        name: campo.name,
        dataType: campo.dataType,
        opcoes: opcoesDoCampoGhl(campo),
    };
}

/**
 * Empurra o catálogo de produtos do desk para o dropdown "Produto Vendido" do GHL.
 *
 * ESTRITAMENTE ADITIVA. `options` é a UNIÃO `opções que já existem no GHL` + `labels do desk que
 * faltam lá`, nesta ordem — nunca a lista do desk sozinha. Duas razões:
 *  1. O PUT do GHL substitui a lista inteira; mandar só o catálogo do desk APAGARIA qualquer
 *     opção que exista apenas no CRM, orfanando as oportunidades históricas que a usam.
 *  2. Remover é uma decisão de quem opera o CRM, não efeito colateral de salvar o catálogo.
 *     Por isso esta função nem expõe `permitirRemocao` — a rota aceita o parâmetro, mas quem
 *     precisa remover faz isso deliberadamente, não por um "salvar" na tela de produtos.
 *
 * Se nada falta, NÃO chama a rota de escrita (`chamouGhl: false`): salvar produto sem mudança de
 * catálogo não deve gerar um PUT no CRM.
 *
 * @returns {Promise<{fieldId: string, antes: string[], depois: string[], adicionadas: string[], chamouGhl: boolean, diff: object}>}
 */
export async function sincronizarCatalogoProdutosGhl(produtos) {
    const campo = await getGhlCampoProdutoVendido();
    const antes = campo.opcoes;
    const diff = diffCatalogoProdutos(produtos, antes);

    if (diff.faltamNoGhl.length === 0) {
        return { fieldId: campo.id, antes, depois: antes, adicionadas: [], chamouGhl: false, diff };
    }

    const options = [...antes, ...diff.faltamNoGhl];

    let res;
    try {
        // A rota escreve no CRM e por isso exige sessão. Sem este cabeçalho ela responde 401 —
        // que é o comportamento certo: um POST anônimo não pode acrescentar produto ao dropdown.
        const { data: { session } = {} } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Faça login novamente para sincronizar o catálogo com o GHL.');

        res = await fetch('/api/ghl-custom-field-options', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({
                fieldId: campo.id,
                name: campo.name,
                dataType: campo.dataType,
                options,
            }),
        });
    } catch (e) {
        logError(e, { fn: 'sincronizarCatalogoProdutosGhl', fieldId: campo.id });
        throw e;
    }

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        const erro = new Error(json?.error ?? `GHL sync catálogo: HTTP ${res.status}`);
        logError(erro, { fn: 'sincronizarCatalogoProdutosGhl', status: res.status, fieldId: campo.id, resposta: json });
        throw erro;
    }

    const depois = Array.isArray(json.depois) && json.depois.length > 0 ? json.depois : options;
    return {
        fieldId: campo.id,
        antes,
        depois,
        adicionadas: Array.isArray(json.adicionadas) ? json.adicionadas : diff.faltamNoGhl,
        chamouGhl: true,
        // Recalculado sobre o estado FINAL: é o que a tela deve mostrar depois de sincronizar.
        diff: diffCatalogoProdutos(produtos, depois),
    };
}

// ─── ghl_stage_map (colunas-gatilho) ───────────────────────────────────────────

export async function loadStageMap() {
    const { data, error } = await supabase.from('ghl_stage_map').select('*');
    if (error) { logError(error, { fn: 'loadStageMap' }); throw error; }
    return data ?? [];
}

/**
 * Salva o mapa completo: estágios com evento → upsert (ativo=true);
 * estágios desmapeados que existiam → ativo=false (histórico preservado).
 */
export async function saveStageMap(rows) {
    const mapped = rows.filter((r) => r.evento);
    const cleared = rows.filter((r) => !r.evento && r.existente);

    if (mapped.length) {
        const payload = mapped.map((r) => ({
            pipeline_id: r.pipeline_id,
            stage_id: r.stage_id,
            evento: r.evento,
            rodada: r.rodada ?? null,
            ativo: true,
        }));
        const { error } = await supabase
            .from('ghl_stage_map')
            .upsert(payload, { onConflict: 'pipeline_id,stage_id' });
        if (error) { logError(error, { fn: 'saveStageMap', step: 'upsert' }); throw error; }
    }
    for (const r of cleared) {
        const { error } = await supabase
            .from('ghl_stage_map')
            .update({ ativo: false })
            .eq('pipeline_id', r.pipeline_id)
            .eq('stage_id', r.stage_id);
        if (error) { logError(error, { fn: 'saveStageMap', step: 'clear' }); throw error; }
    }
}

// ─── ghl_field_map (de-para de campos) ─────────────────────────────────────────

export async function loadFieldMap() {
    const { data, error } = await supabase.from('ghl_field_map').select('*').order('created_at');
    if (error) { logError(error, { fn: 'loadFieldMap' }); throw error; }
    return data ?? [];
}

export async function upsertFieldMapRow(row) {
    const { error } = await supabase.from('ghl_field_map').upsert(
        {
            evento: row.evento,
            ghl_field_id: row.ghl_field_id,
            ghl_field_key: row.ghl_field_key ?? null,
            destino_tabela: row.destino_tabela,
            destino_coluna: row.destino_coluna,
            tipo_esperado: row.tipo_esperado ?? null,
            obrigatorio: !!row.obrigatorio,
            ativo: true,
        },
        { onConflict: 'evento,ghl_field_id,destino_coluna' },
    );
    if (error) { logError(error, { fn: 'upsertFieldMapRow' }); throw error; }
}

export async function deactivateFieldMapRow(id) {
    const { error } = await supabase.from('ghl_field_map').update({ ativo: false }).eq('id', id);
    if (error) { logError(error, { fn: 'deactivateFieldMapRow' }); throw error; }
}
